/**
 * StatusPill — the conversation's handling state, as the back office reports it
 * (055 FR-012/FR-013). Shared by the Viestit list row and the conversation
 * detail header.
 *
 * Two rules, both load-bearing:
 *
 *   1. The LABEL is the back office's own `status.name`, rendered verbatim.
 *      Oma+ never renames, translates, or substitutes it. When the back office
 *      supplies nothing usable, NO pill renders — an absent state is shown as
 *      absent, not as a default (the prototype's own `formatMessageStatus`
 *      silently defaults unknown values to "Vastaanotettu"; that is a demo
 *      convenience and would be a fabricated claim about a member's case here).
 *
 *   2. The COLOUR comes from `resolveConversationStatusVariant`, never from the
 *      label text. Today that means: grey for a closed conversation (from the
 *      contract-authoritative `closedAt`), neutral for everything else, because
 *      the state-id map is a blocked parameter (BP-001 — see
 *      `conversation-status.ts`).
 *
 * Styling mirrors the prototype (`src/lib/messageStatus.ts`): one base colour per
 * variant, composed with Tailwind opacity modifiers into border/fill/text. The
 * amber `progress` variant is the one that needs a separate darker foreground —
 * the amber itself cannot carry text at 4.5:1.
 */

import type { JSX } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';

import {
  type ConversationStatus,
  conversationStatusLabel,
  resolveConversationStatusVariant,
} from './conversation-status.js';

const VARIANT_CLASSES = {
  received: 'border-status-received/30 bg-status-received/10 text-status-received',
  progress: 'border-status-progress/35 bg-status-progress/15 text-status-progress-foreground',
  closed: 'border-status-closed/30 bg-status-closed/10 text-status-closed',
  // Neutral is not a colour choice — it is the ABSENCE of one, used whenever the
  // state's meaning has not been authorised (BP-001).
  neutral: 'border-border bg-muted text-muted-foreground',
} as const;

const SIZE_CLASSES = {
  /** Viestit list row — the prototype's larger, more legible row pill. */
  row: 'px-3.5 py-1.5 text-sm',
  /** Conversation detail header — the prototype's compact header pill. */
  header: 'px-2.5 py-0.5 text-xs',
} as const;

export interface ConversationStatusPillProps {
  readonly status: ConversationStatus;
  /**
   * Closure timestamp, or null/undefined for open-or-unknown. Drives the closed
   * variant. `undefined` is explicit in the type (not just optional) because
   * `exactOptionalPropertyTypes` is on and the projection genuinely carries all
   * three states — the contract treats null and absent identically.
   */
  readonly closedAt?: string | null | undefined;
  readonly size?: keyof typeof SIZE_CLASSES;
}

/**
 * Renders the state pill, or `null` when there is no usable label to show.
 */
export function ConversationStatusPill({
  status,
  closedAt,
  size = 'row',
}: ConversationStatusPillProps): JSX.Element | null {
  const t = useTranslate();
  const label = conversationStatusLabel(status);
  if (label === null) {
    return null;
  }
  const variant = resolveConversationStatusVariant(status, closedAt);
  return (
    <span
      className={
        'inline-flex max-w-full items-center rounded-full border font-semibold leading-tight ' +
        `whitespace-normal break-words sm:whitespace-nowrap ${SIZE_CLASSES[size]} ${VARIANT_CLASSES[variant]}`
      }
    >
      {/* Sighted readers infer "this is a status" from the pill's shape and
          position; a screen reader would otherwise hear the bare state word
          after the subject with nothing saying what it is. The prefix is
          visually hidden so nothing changes on screen (owner decision
          2026-09-30). */}
      {/* NBSP, not a plain space: a trailing space at the end of a flex item is
          collapsed, which would run the prefix into the label. */}
      <span className="sr-only">{`${t('myCases.statusPrefix')}\u00a0`}</span>
      {label}
    </span>
  );
}
